import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useBlockActions } from "@/contexts/blockContext";
import { useRootPagesOrder } from "@/contexts/rootPagesOrderContext";
import { useRestampMovedSubtree } from "@/hooks/use-restampMovedSubtree";
import { toast } from "@/lib/toast";
import {
  useLocalSidebarOrder,
  removePageFromAllMutation,
  movePageToPrivateMutation,
  movePageToPublicMutation,
  updateBlockFieldsMutation,
  trashPageMutation,
  deletePageMutation,
  movePageMutation,
  addTemplatePageMutation,
  movePageToWorkAreaMutation,
  getBlock as getBlockFromIDB,
} from "@/indexedDB";
import type { PageMovePayload } from "@/indexedDB";
import { removeRecentNoteId } from "@/lib/recent-notes";

export default function useNoteActions() {
  const { currentWorkspace } = useWorkspaceContext();
  const { updateBlock, getBlock } = useBlockActions();
  // Menu-driven section changes (Move to public/private, Convert to template) go
  // through movePageMutation → the same drag-and-drop endpoint, so the backend
  // re-stamps descendants; mirror that locally so the subtree updates without reload.
  const restampMovedSubtree = useRestampMovedSubtree(currentWorkspace?._id ?? "");
  const {
    privatePagesOrder,
    publicPagesOrder,
    templatePagesOrder,
    workAreaPagesOrder,
  } = useLocalSidebarOrder(currentWorkspace?._id || "");
  // The sidebar resolves each section as `fromIDB.length > 0 ? fromIDB : fromContext`
  // (Sidebar.tsx), so when a move empties a section the IDB array goes to length 0
  // and the stale context array takes over — the moved page keeps rendering in the
  // section it just left. Every mutation below therefore updates the context too,
  // the same pairing DeleteNote already does (removePageFromAllMutation + removePage).
  const {
    removePage,
    movePageToPrivate,
    movePageToPublic,
    movePageToWorkArea,
    addTemplatePage,
    removeTemplatePage,
  } = useRootPagesOrder();

  const UpdateNote = async (
    id: string,
    title: string,
    icon: string | null = null,
  ) => {
    try {
      const workspaceId = currentWorkspace?._id;
      if (!workspaceId) {
        console.error("Workspace not found");
        return null;
      }

      // Read from IDB — source of truth
      const currentBlock = await getBlockFromIDB(id);
      if (!currentBlock) {
        console.error("Block not found:", id);
        return null;
      }

      const updatedValue = {
        ...currentBlock.value,
        title: title,
        icon: icon || (currentBlock.value as any).icon || "",
      };

      // Optimistic context update — instant UI feedback before IDB write
      updateBlock(id, { value: updatedValue });

      // Write to IDB + enqueue BLOCK_CHANGES — SyncWatcher calls batch-update in background
      await updateBlockFieldsMutation({
        blockId: id,
        noteId: id,
        updates: { value: updatedValue },
        workspaceId,
        parentId: currentBlock.parentId,
      });
    } catch (err) {
      console.error("Error in UpdateNote:", err);
      toast.error("Error in UpdateNote");
      return null;
    }
  };

  const DeleteNote = async (blockId: string) => {
    try {
      const workspaceId = currentWorkspace?._id;
      if (!workspaceId) {
        console.error("Workspace not found");
        toast.error("Workspace not found");
        return null;
      }

      // Read from IDB — source of truth
      const currentBlock = await getBlockFromIDB(blockId);
      if (!currentBlock) {
        console.error("Block not found:", blockId);
        toast.error("Page not found");
        return null;
      }

      const pageType = (currentBlock.value as any).pageType;
      const isRootPage =
        currentBlock.parentType === "workspace" ||
        currentBlock.parentType === "workarea" ||
        currentBlock.parentType === "collection";
      // Datasource rows are permanently deleted, not trashed: they don't live
      // in the sidebar and can't be restored from Trash, so trashing one just
      // orphans a row nobody can recover.
      const isDatasourceRow = pageType === "Viewdatabase_Note";

      // Remove from sidebar order immediately (skipSync=true — no SIDEBAR_REORDER op needed,
      // the backend restores/cleans sidebar on restore/permanent-delete).
      if (isRootPage && !isDatasourceRow) {
        await removePageFromAllMutation(blockId, workspaceId, true);
        removePage(blockId);
      }

      // Context readers filter by status; without this mirror the page stays visible.
      updateBlock(blockId, { status: "deleted" });

      if (isDatasourceRow) {
        await deletePageMutation({
          blockId,
          workspaceId,
          parentId: currentBlock.parentId,
          parentType: currentBlock.parentType,
        });
        removeRecentNoteId(blockId, workspaceId);
        toast.success("Page deleted");
        return true;
      }

      await trashPageMutation({
        blockId,
        workspaceId,
        parentId: currentBlock.parentId,
        parentType: currentBlock.parentType,
      });

      //remove from the recent notes
      removeRecentNoteId(blockId, workspaceId);

      toast.success("Page moved to trash");
      return true;
    } catch (err) {
      console.error("Error in DeleteNote:", err);
      toast.error("Error deleting the note");
      return null;
    }
  };

  const MoveNote = async (noteId: string, isPublicNote: boolean, isRestrictedPage: boolean) => {
    try {
      // Read from IDB — source of truth
      const currentBlock = await getBlockFromIDB(noteId);
      if (!currentBlock) {
        console.error("Block not found:", noteId);
        toast.error("Page not found");
        return null;
      }

      const workspaceId = currentWorkspace?._id;
      if (!workspaceId) {
        toast.error("Workspace not found");
        return null;
      }

      // Determine new pageType
      let newPageType: "private" | "public" | "restricted";
      if (isPublicNote) {
        newPageType = isRestrictedPage ? "restricted" : "public";
      } else {
        newPageType = "private";
      }

      // --- Check if source is a child page or a root page ---
      const isChildPage = currentBlock.parentType === "page";
      const sourceParentId = currentBlock.parentId;

      // --- Destination order (always root public/private) ---
      const destOrder = isPublicNote ? [...publicPagesOrder] : [...privatePagesOrder];
      const newDestOrder = [...destOrder.filter((id) => id !== noteId), noteId];

      // --- Lean payload: the moved page + its destination + position. The source
      //     parent is derived from the block itself (no per-parent arrays). ---
      const dragAndDropPayload: PageMovePayload = {
        updatedBlockInfo: {
          blockId: noteId,
          newParentType: "workspace",
          newParentId: workspaceId,
          newPageType: newPageType,
          newWorkareaId: null,
          insertAfterBlockId: newDestOrder[newDestOrder.indexOf(noteId) - 1] ?? null,
        },
      };

      // Optimistic re-root. workareaId is cleared because the page is leaving any
      // work area it was in — mirrors SidebarNode.handleDrop's drag path, which
      // sets workareaId null for every non-workarea destination.
      updateBlock(noteId, {
        value: { ...(currentBlock.value as any), pageType: newPageType },
        parentType: "workspace",
        parentId: workspaceId,
        workareaId: null,
      });

      // Optimistically remove it from its old parent's children in context.
      if (isChildPage && sourceParentId) {
        const sourceParent = getBlock(sourceParentId);
        if (sourceParent) {
          updateBlock(sourceParentId, {
            blockIds: (sourceParent.blockIds || []).filter((cid: string) => cid !== noteId),
          });
        }
      }

      // --- Write to IDB + enqueue PAGE_MOVE (SyncWatcher calls drag-and-drop API) ---
      // movePageMutation atomically:
      //   1. Updates block: pageType, parentType → "workspace", parentId → workspaceId
      //   2. Removes noteId from source parent blockIds (if child page)
      //   3. Enqueues PAGE_MOVE op
      await movePageMutation({
        noteId,
        workspaceId,
        newPageType,
        sourceParentId: isChildPage ? sourceParentId : null,
        sourceParentType: currentBlock.parentType,
        dragAndDropPayload,
        newWorkareaId: null,
      });

      // After the mutation so a rolled-back move can't orphan descendants in IDB.
      await restampMovedSubtree(noteId, newPageType, null);

      // Update sidebar order in IDB only — no SIDEBAR_REORDER op.
      // The drag-and-drop API (called via PAGE_MOVE → SyncWatcher) already updates
      // the blockIdArrays server-side, so a separate sidebar-order API call would be redundant.
      if (isPublicNote) {
        await movePageToPublicMutation(noteId, workspaceId, undefined, true);
      } else {
        await movePageToPrivateMutation(noteId, workspaceId, undefined, true);
      }
      // Mirror into the context order (see the note at the top of this hook).
      // removePage doesn't cover templates, so drop that membership explicitly.
      removeTemplatePage(noteId);
      if (isPublicNote) movePageToPublic(noteId);
      else movePageToPrivate(noteId);

      toast.success(`Page moved to ${newPageType} pages`);
      return true;
    } catch (err) {
      console.error("Error in MoveNote:", err);
      toast.error("Error moving page");
      return null;
    }
  };

  // Convert a page into a template (in place). Mirrors MoveNote, but moves the
  // page into the Templates bucket — same drag-and-drop move "drop a note on the
  // Templates section" uses.
  const ConvertNoteToTemplate = async (noteId: string) => {
    try {
      const workspaceId = currentWorkspace?._id;
      if (!workspaceId) {
        toast.error("Workspace not found");
        return null;
      }

      // Read from IDB — source of truth
      const currentBlock = await getBlockFromIDB(noteId);
      if (!currentBlock) {
        toast.error("Page not found");
        return null;
      }

      if ((currentBlock.value as any)?.isTemplate === true) {
        toast.message("This page is already a template");
        return true;
      }

      const isChildPage = currentBlock.parentType === "page";
      const sourceParentId = currentBlock.parentId;
      // --- Destination: workspace-root Templates bucket ---
      const newTemplateOrder = [...templatePagesOrder.filter((id) => id !== noteId), noteId];
      const dragAndDropPayload: PageMovePayload = {
        updatedBlockInfo: {
          blockId: noteId,
          newParentType: "workspace",
          newParentId: workspaceId,
          newPageType: "template",
          newWorkareaId: null,
          insertAfterBlockId: newTemplateOrder[newTemplateOrder.indexOf(noteId) - 1] ?? null,
        },
      };

      // Optimistic re-root + flag in context so the UI reflects it instantly.
      const newValue = {
        ...(currentBlock.value as any),
        isTemplate: true,
        pageType: "template",
      };
      // workareaId cleared for the same reason as MoveNote — the page is leaving
      // whatever work area it was in.
      updateBlock(noteId, { value: newValue, parentType: "workspace", parentId: workspaceId, workareaId: null });

      // Optimistically remove it from its old parent's children in context.
      if (isChildPage && sourceParentId) {
        const sourceParent = getBlock(sourceParentId);
        if (sourceParent) {
          updateBlock(sourceParentId, {
            blockIds: (sourceParent.blockIds || []).filter((cid: string) => cid !== noteId),
          });
        }
      }

      // One drag-and-drop move: removes noteId from its old section/parent, adds it
      // to templatePageIds, re-roots it, and sets pageType + isTemplate server-side.
      await movePageMutation({
        noteId,
        workspaceId,
        newPageType: "template",
        sourceParentId: isChildPage ? sourceParentId : null,
        sourceParentType: currentBlock.parentType,
        dragAndDropPayload,
        newWorkareaId: null,
      });

      // After the mutation so a rolled-back move can't orphan descendants in IDB.
      await restampMovedSubtree(noteId, "template", null);

      // Mirror the section change in the local IDB order (skipSync — the move
      // above already rewrote these arrays on the server).
      await removePageFromAllMutation(noteId, workspaceId, true);
      await addTemplatePageMutation(noteId, workspaceId, undefined, true);
      // Mirror into the context order (see the note at the top of this hook).
      removePage(noteId);
      addTemplatePage(noteId);

      toast.success("Template created");
      return true;
    } catch (err) {
      console.error("Error in ConvertNoteToTemplate:", err);
      toast.error("Error creating template");
      return null;
    }
  };

  // Move a page into a work area. Mirrors ConvertNoteToTemplate, but the
  // destination is the work area itself rather than the workspace root — so the
  // page re-parents onto the work area and carries a workareaId. Same recipe the
  // "drop a page onto a work area page" path in SidebarNode.handleDrop uses.
  const MoveNoteToWorkArea = async (noteId: string, workAreaId: string) => {
    try {
      const workspaceId = currentWorkspace?._id;
      if (!workspaceId) {
        toast.error("Workspace not found");
        return null;
      }

      // Read from IDB — source of truth
      const currentBlock = await getBlockFromIDB(noteId);
      if (!currentBlock) {
        toast.error("Page not found");
        return null;
      }

      // Parent fields, not workareaId — see the matching note in SectionDropZone:
      // workareaId can be stale on a page that has since left a work area.
      if (currentBlock.parentType === "workarea" && currentBlock.parentId === workAreaId) {
        return true; // Already in this work area
      }

      const isChildPage = currentBlock.parentType === "page";
      const sourceParentId = currentBlock.parentId;

      // --- Destination: the work area's own root list ---
      const destOrder = [...(workAreaPagesOrder[workAreaId] || []).filter((id) => id !== noteId), noteId];
      const dragAndDropPayload: PageMovePayload = {
        updatedBlockInfo: {
          blockId: noteId,
          newParentType: "workarea",
          newParentId: workAreaId,
          newPageType: "workarea",
          newWorkareaId: workAreaId,
          insertAfterBlockId: destOrder[destOrder.indexOf(noteId) - 1] ?? null,
        },
      };

      // Optimistic re-parent onto the work area so the UI reflects it instantly.
      updateBlock(noteId, {
        value: { ...(currentBlock.value as any), pageType: "workarea", isTemplate: false },
        parentType: "workarea",
        parentId: workAreaId,
        workareaId: workAreaId,
      });

      // Optimistically remove it from its old parent's children in context.
      if (isChildPage && sourceParentId) {
        const sourceParent = getBlock(sourceParentId);
        if (sourceParent) {
          updateBlock(sourceParentId, {
            blockIds: (sourceParent.blockIds || []).filter((cid: string) => cid !== noteId),
          });
        }
      }

      await movePageMutation({
        noteId,
        workspaceId,
        newPageType: "workarea",
        sourceParentId: isChildPage ? sourceParentId : null,
        sourceParentType: currentBlock.parentType,
        dragAndDropPayload,
        newParentType: "workarea",
        newParentId: workAreaId,
        newWorkareaId: workAreaId,
      });

      // After the mutation so a rolled-back move can't orphan descendants in IDB.
      await restampMovedSubtree(noteId, "workarea", workAreaId);

      // Mirror the section change in the local IDB order (skipSync — the move
      // above already rewrote these arrays on the server). This deliberately
      // mirrors MoveNote's movePageTo*Mutation rather than
      // removePageFromAllMutation, which would also strip the page's favourite.
      await movePageToWorkAreaMutation(workAreaId, noteId, workspaceId, undefined, true);
      // Mirror into the context order (see the note at the top of this hook) —
      // without this, dragging the LAST page out of a section leaves the stale
      // context array in play, so that section never reads as empty and its own
      // drop zone never mounts.
      removeTemplatePage(noteId);
      movePageToWorkArea(workAreaId, noteId);

      toast.success("Page moved to work area");
      return true;
    } catch (err) {
      console.error("Error in MoveNoteToWorkArea:", err);
      toast.error("Error moving page");
      return null;
    }
  };

  return {
    UpdateNote,
    DeleteNote,
    MoveNote,
    ConvertNoteToTemplate,
    MoveNoteToWorkArea,
  };
}
